import { useSelector } from 'react-redux';
import { useCallback } from 'react';

import { Segment } from '../../services/analytics-services/segment';
import { Yofi } from '../../services/analytics-services/yofi';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import {
  BULK_LABEL_CREATION_COMPLETE,
  BULK_LABEL_CREATION_START,
  BULK_LABEL_PRINTING_COMPLETE,
  BULK_LABEL_PRINTING_START,
  LABEL_CREATION_COMPLETE,
  LABEL_CREATION_ERROR,
  LABEL_CREATION_START,
  LABEL_PRINTING_COMPLETE,
} from '../../constants/analytics-events';
import { Workflows } from '../../tracking/tracking-constants';
import { LocalStorageService } from '../../services/local-storage-service';
import Global from '../../store/global-selectors';

// Martin Fowler ftw!
const trackLabelWorkflowEvent = (workflow, event, eventData) => {
  Segment.trackEvent(event, eventData);
  Yofi.applySessionMetadataForWorkflow(workflow, event); // TODO: DAT - only track wallet purchases?
};

export const getPlansWithDangerousGoods = (fulfillmentPlanIds, state) => {
  const fulfillmentPlans = fulfillmentPlanIds.map((fulfillmentPlanId) =>
    Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }),
  );

  return fulfillmentPlans.reduce((acc, plan) => {
    if (plan?.items?.some((item) => item?.dangerousGoods?.length)) {
      return [...acc, plan.fulfillmentPlanId];
    }
    return acc;
  }, []);
};

export const useTrackLabelCreationStartFunc = (fulfillmentPlanIds = emptyArray, source = '') => {
  const sellerLabelProviderIds = useSelector((state) => {
    return fulfillmentPlanIds.map((fulfillmentPlanId) =>
      Global.selectEntitiesFulfillmentPlanSellerLabelProviderID(state, {
        fulfillmentPlanId,
      }),
    );
  });

  const hasWalletProvider = useSelector((state) => {
    return sellerLabelProviderIds.some((sellerLabelProviderId) =>
      Global.selectSellerLabelProviderIsWalleted(state, {
        sellerLabelProviderId,
      }),
    );
  });

  return useCallback(() => {
    trackLabelWorkflowEvent(Workflows.LABEL_PURCHASE_FLOW, LABEL_CREATION_START, {
      source,
      fulfillmentPlanIds,
      hasWalletProvider,
      // The lift to convert a handful of tracking events off this flag would blow scope.
      // SPD-16505 to track final clean up of this.
      enablePaymentsAndSubscriptionSettings: true,
    });
  }, [source, fulfillmentPlanIds, hasWalletProvider]);
};

export const trackBulkLabelCreationStart = (
  fulfillmentPlanIds = emptyArray,
  hasWalletProvider = false,
  enablePaymentsAndSubscriptionSettings = true,
) => {
  trackLabelWorkflowEvent(Workflows.BULK_LABEL_PURCHASE_FLOW, BULK_LABEL_CREATION_START, {
    fulfillmentPlanIds,
    hasWalletProvider,
    enablePaymentsAndSubscriptionSettings,
  });
};

export const trackLabelCreationComplete = (
  fulfillmentPlanId = '',
  labelObject = emptyObject,
  doNotPrint = false,
  hasWalletProvider = false,
  plansWithDangerousGoods,
  insurancePreference,
) => {
  const isFirstLabel = !LocalStorageService.getItem('seller_has_created_label');

  trackLabelWorkflowEvent(Workflows.LABEL_PURCHASE_FLOW, LABEL_CREATION_COMPLETE, {
    fulfillmentPlanId,
    labelObject,
    doNotPrint,
    isFirstLabel,
    hasWalletProvider,
    enablePaymentsAndSubscriptionSettings: true,
    plansWithDangerousGoods,
    insurancePreference,
  });
};

export const trackBulkLabelCreationComplete = (
  fpIdsWithLabelsCreated = emptyArray,
  erroredFulfillmentPlanIds = emptyArray,
  excludedFulfillmentPlanIds = emptyArray,
  labelBatchId = '',
  hasWalletProvider = false,
  plansWithDangerousGoods,
) => {
  const isFirstTimeLabelCreation = !LocalStorageService.getItem('seller_has_created_label');

  trackLabelWorkflowEvent(Workflows.BULK_LABEL_PURCHASE_FLOW, BULK_LABEL_CREATION_COMPLETE, {
    fpIdsWithLabelsCreated,
    erroredFulfillmentPlanIds,
    excludedFulfillmentPlanIds,
    labelBatchId,
    isFirstTimeLabelCreation,
    hasWalletProvider,
    enablePaymentsAndSubscriptionSettings: true,
    plansWithDangerousGoods,
  });
};

export const trackLabelPrintingComplete = (fulfillmentPlanId = '') => {
  Segment.trackEvent(LABEL_PRINTING_COMPLETE, {
    fulfillmentPlanId,
  });
};
export const trackBulkLabelPrintingStart = (fulfillmentPlanIds = emptyArray, labelBatchId = '') => {
  Segment.trackEvent(BULK_LABEL_PRINTING_START, {
    fulfillmentPlanIds,
    labelBatchId,
  });
};
export const trackBulkLabelPrintingComplete = (printedFulfillmentPlanIds = emptyArray) => {
  Segment.trackEvent(BULK_LABEL_PRINTING_COMPLETE, {
    printedFulfillmentPlanIds,
  });
};

export const trackLabelCreationError = (fulfillmentPlanId = '', friendlyErrors = emptyArray) => {
  trackLabelWorkflowEvent(Workflows.LABEL_PURCHASE_FLOW, LABEL_CREATION_ERROR, {
    fulfillmentPlanId,
    friendlyErrors,
  });
};
